import { Notice, type Tone } from '@rental/ui'
import { type ReactNode, useEffect, useRef } from 'react'

/**
 * What an action just did, said where it happened (DESIGN.md, "A screen and
 * its parts"). The control that was pressed is often gone once it has: a form
 * that empties, a step that is done, a button whose reason to be has passed.
 * A focus left on nothing leaves a keyboard and a screen reader nowhere, so
 * the outcome takes the focus as it appears. It is read once, by the focus,
 * and is no live region as well. Draw it only after an action, with a `key`
 * that changes with the outcome.
 */
export function OutcomeNotice({
  tone = 'success',
  children,
}: {
  tone?: Tone
  children: ReactNode
}) {
  return (
    <OutcomeFocus>
      <Notice tone={tone}>{children}</Notice>
    </OutcomeFocus>
  )
}

/** The same, for an outcome said in more than one notice: the focus reads them all. */
export function OutcomeFocus({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // A question that closes gives the focus back to the button that opened it, wherever
    // that is: the outcome takes it from a button anywhere, never from a field elsewhere.
    if (box.current && mayTakeFocus(box.current.closest('section'), { fromButtons: true })) {
      box.current.focus()
    }
  }, [])
  return (
    <div ref={box} tabIndex={-1} className="flex flex-col gap-4 rounded-panel focus:outline-none">
      {children}
    </div>
  )
}

/** Where the counter types or chooses. */
const FIELDS = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])'

/**
 * Whether an outcome may take the focus now. Always from nothing (the control
 * that was pressed has gone) and from inside its own part of the screen, where
 * the action was. Never from a field elsewhere: the counter has moved on to it
 * while the answer came. From a button elsewhere only with `fromButtons`.
 */
export function mayTakeFocus(
  region: Element | null,
  { fromButtons }: { fromButtons: boolean },
): boolean {
  const active = document.activeElement
  if (active === null || active === document.body) return true
  if (region?.contains(active)) return true
  return fromButtons && !active.matches(FIELDS)
}
